import { useState, useCallback, useRef } from 'react';
import { fetchData } from '../helpers/apiUtils';

interface ReachabilityResult {
  reachable: boolean;
  status_code: number | null;
  redirect_url: string | null;
  cross_domain_redirect: boolean;
  error: string | null;
}

interface ValidationResponse {
  valid_format: boolean;
  cleaned_url: string | null;
  reachability: ReachabilityResult | null;
  error?: string;
}

interface FieldState {
  validating: boolean;
  reachable: boolean | null;
  error: string | null;
  warning: string | null;
  cross_domain_redirect: boolean;
  redirect_url: string | null;
}

const EMPTY_STATE: FieldState = {
  validating: false,
  reachable: null,
  error: null,
  warning: null,
  cross_domain_redirect: false,
  redirect_url: null,
};

const STEALTHSEMINAR_DOMAINS = ['webinar.finanztrends.de', 'joinnow.live'];
const STEALTHSEMINAR_WARNING_FIELDS = ['doi_url', 'signup_url'];

function getStealthSeminarWarning(fieldName: string, url: string): string | null {
  if (!STEALTHSEMINAR_WARNING_FIELDS.includes(fieldName)) return null;
  try {
    const hostname = new URL(url.trim()).hostname;
    if (STEALTHSEMINAR_DOMAINS.includes(hostname)) {
      return `StealthSeminar-Domain (${hostname}) — vermutlich falscher Link für dieses Feld`;
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

export function useUrlValidation() {
  const [fields, setFields] = useState<Record<string, FieldState>>({});
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const getState = useCallback(
    (fieldName: string): FieldState => {
      return fields[fieldName] || EMPTY_STATE;
    },
    [fields],
  );

  const resetField = useCallback((fieldName: string) => {
    if (debounceTimers.current[fieldName]) {
      clearTimeout(debounceTimers.current[fieldName]);
      delete debounceTimers.current[fieldName];
    }
    setFields((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    Object.values(debounceTimers.current).forEach(clearTimeout);
    debounceTimers.current = {};
    setFields({});
  }, []);

  const validateUrl = useCallback(
    (fieldName: string, url: string) => {
      // Clear previous timer
      if (debounceTimers.current[fieldName]) {
        clearTimeout(debounceTimers.current[fieldName]);
      }

      // Empty URL = clear state (field is optional)
      if (!url || !url.trim()) {
        resetField(fieldName);
        return;
      }

      // Instant client-side format check
      try {
        new URL(url.trim());
      } catch {
        setFields((prev) => ({
          ...prev,
          [fieldName]: {
            ...EMPTY_STATE,
            reachable: false,
            error: 'Ungültiges URL-Format',
          },
        }));
        return;
      }

      // Debounce the server call
      setFields((prev) => ({
        ...prev,
        [fieldName]: { ...EMPTY_STATE, validating: true },
      }));

      debounceTimers.current[fieldName] = setTimeout(async () => {
        try {
          const response = await fetchData<ValidationResponse>(
            '/api/webinar-management/validate-url',
            {
              method: 'POST',
              body: JSON.stringify({ url: url.trim() }),
            },
          );

          if (!response.success || !response.data) {
            setFields((prev) => ({
              ...prev,
              [fieldName]: {
                ...EMPTY_STATE,
                reachable: false,
                error: response.error || 'Validierung fehlgeschlagen',
              },
            }));
            return;
          }

          const data = response.data;

          if (!data.valid_format) {
            setFields((prev) => ({
              ...prev,
              [fieldName]: {
                ...EMPTY_STATE,
                reachable: false,
                error: data.error || 'Ungültiges URL-Format',
              },
            }));
            return;
          }

          const reachability = data.reachability!;
          const domainWarning = getStealthSeminarWarning(fieldName, url);
          setFields((prev) => ({
            ...prev,
            [fieldName]: {
              validating: false,
              reachable: reachability.reachable,
              error: reachability.error,
              warning: domainWarning,
              cross_domain_redirect: reachability.cross_domain_redirect,
              redirect_url: reachability.redirect_url,
            },
          }));
        } catch {
          setFields((prev) => ({
            ...prev,
            [fieldName]: {
              ...EMPTY_STATE,
              reachable: false,
              error: 'Netzwerkfehler bei Validierung',
            },
          }));
        }
      }, 500);
    },
    [resetField],
  );

  const hasUnreachableUrls = useCallback((): boolean => {
    return Object.values(fields).some(
      (state) => state.reachable === false && !state.cross_domain_redirect,
    );
  }, [fields]);

  const isValidating = useCallback((): boolean => {
    return Object.values(fields).some((state) => state.validating);
  }, [fields]);

  return {
    validateUrl,
    getState,
    resetField,
    resetAll,
    hasUnreachableUrls,
    isValidating,
  };
}
